import appInsightsLogger from "../logging/appInsightsLogger"

type Base64ImageData = {
  key: string
  base64: string
  timestamp: number
  pageURL: string // URL associated to the page the image is used on, used for cleanup when the page is closed or navigated away from
}
const BASE64_SUFFIX = "_base64"

/**
 * Ensures the storage key has the correct suffix for base64 images.
 *
 * @param key The original storage key.
 * @returns The storage key with the base64 suffix.
 */
const getBase64StorageKey = (key: string) => {
  return key.endsWith(BASE64_SUFFIX) ? key : `${key}${BASE64_SUFFIX}`
}

/**
 * Stores a base64-encoded image in localStorage with a timestamp.
 *
 * WARNING: This function does not handle quota exceeded errors. Use saveBase64ImageWithEviction for safer storage that evicts old images when necessary.
 *
 * @param key The key under which to store the image.
 * @param base64String The base64-encoded image string.
 * @returns void
 */
function setBase64ImageInStorage(key: string, base64String: string) {
  const storageKey = getBase64StorageKey(key)
  const value: Omit<Base64ImageData, "key"> = {
    base64: base64String,
    timestamp: Date.now(),
    pageURL: window.location.href,
  }
  localStorage.setItem(storageKey, JSON.stringify(value))
}

/**
 * Retrieves a base64-encoded image from localStorage.
 *
 * @param key The key under which the image is stored.
 * @returns The base64-encoded image string, or an empty string if not found.
 */
export function getBase64ImageFromStorage(key: string): string {
  const storageKey = getBase64StorageKey(key)
  const item = localStorage.getItem(storageKey)
  const value = item ? JSON.parse(item) : null

  // update the timestamp to reflect recent access, which can help with eviction decisions
  if (value) {
    setBase64ImageInStorage(key, value.base64)
  }

  return value?.base64 ?? ""
}

/**
 * Gets all base64-encoded images from localStorage.
 *
 * @returns An array of objects containing the key, base64 string, and timestamp.
 */
function getAllBase64ImagesFromStorage(): Base64ImageData[] {
  const keys: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key && key.endsWith(BASE64_SUFFIX)) keys.push(key)
  }

  const images = keys.map((key) => {
    const item = localStorage.getItem(key)
    const value = item ? JSON.parse(item) : null
    return {
      key: key,
      base64: value?.base64 ?? "",
      timestamp: value?.timestamp ?? 0,
      sizeInMB: value?.sizeInMB ?? 0,
      pageURL: value?.pageURL ?? "",
    }
  })

  return images
}

/**
 * Removes a base64-encoded image from localStorage.
 *
 * @param key The key under which the image is stored.
 * @returns void
 */
export const removeBase64ImageFromStorage = (key: string) => {
  // Append suffix to key if not present to ensure we target the correct item in storage
  const storageKey = getBase64StorageKey(key)
  localStorage.removeItem(storageKey)
}

/**
 * Saves a base64-encoded image to localStorage, evicting the oldest image if storage quota is exceeded.
 *
 * @param key The key under which to store the image.
 * @param base64String The base64-encoded image string.
 * @returns void
 */
export function saveBase64ImageWithEviction(key: string, base64String: string) {
  const initialImages = getAllBase64ImagesFromStorage()
  for (let i = 0; i <= initialImages.length; i++) {
    try {
      setBase64ImageInStorage(key, base64String)
      break
    } catch (e) {
      if (isQuotaExceededError(e)) {
        const images = getAllBase64ImagesFromStorage()
        // If there are no images to evict, log the error and exit
        if (images.length === 0) {
          const errorText =
            "LocalStorage quota exceeded with no images to evict."
          appInsightsLogger.traceError(errorText)
          throw new Error(errorText)
        }

        // Remove the oldest, beginning with images from other pages to avoid evicting images that might still be in use on the current page
        images.sort((a, b) => a.timestamp - b.timestamp)
        const imagesOriginatingFromDifferentPage = images.filter(
          (image) => image.pageURL !== window.location.href,
        )
        const imagesToEvict =
          imagesOriginatingFromDifferentPage.length > 0
            ? imagesOriginatingFromDifferentPage
            : images
        localStorage.removeItem(imagesToEvict[0].key)
        appInsightsLogger.traceInfo(
          `LOCALSTORAGE BASE64 EVICTION: evicted ${imagesToEvict[0].key}, ${imagesOriginatingFromDifferentPage.length > 0 ? "different page" : "same page"}, added ${getBase64StorageKey(key)}.`,
        )
      } else {
        throw e
      }
    }
  }
}

/**
 * Removes all base64-encoded images from localStorage.
 * This function can be used for cleanup purposes only.
 *
 * @return void
 */
export const removeAllBase64Images = () => {
  // First delete off expected key suffix
  const expectedBase64Images = getAllBase64ImagesFromStorage()
  for (const image of expectedBase64Images) {
    localStorage.removeItem(image.key)
  }

  // Then delete those with values that includes data:image and base64 - this should clear base 64 images that were stored prior to
  // implementing the structured storage format with the _base64 suffix, ensuring we don't leave orphaned base64 images in storage.
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key) {
      const item = localStorage.getItem(key)
      if (item && item.includes("data:image") && item.includes("base64")) {
        localStorage.removeItem(key)
      }
    }
  }
}

/**
 * Checks if the error is a quota exceeded error.
 *
 * @param e The error object to check.
 * @returns True if the error is a quota exceeded error, false otherwise.
 */
function isQuotaExceededError(e: unknown) {
  return (
    typeof e === "object" &&
    e !== null &&
    "code" in e &&
    "name" in e &&
    (e.code === 22 || e.name === "QuotaExceededError")
  )
}
